import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Search, Download, ChevronLeft, ChevronRight,
  Users, Sparkles, Loader2, Eye, UserCircle2, Crown, Clock, CalendarDays,
  CalendarRange, CalendarCheck2, CalendarClock, Info,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const PERIODS = [
  { key: "24h", label: "24 uur" },
  { key: "7d", label: "7 dagen" },
  { key: "14d", label: "14 dagen" },
  { key: "30d", label: "30 dagen" },
  { key: "60d", label: "60 dagen" },
  { key: "90d", label: "90 dagen" },
  { key: "all", label: "Alle" },
];

const TIERS = [
  { key: "all", label: "Alle" },
  { key: "free", label: "Free" },
  { key: "premium", label: "Premium" },
];

const STATUSES = [
  { key: "all", label: "Alle statussen" },
  { key: "active", label: "Actief" },
  { key: "canceled", label: "Canceled/inactief" },
];

const SORTS = [
  { key: "newest", label: "Nieuwste" },
  { key: "oldest", label: "Oudste" },
  { key: "name", label: "Naam" },
  { key: "tier", label: "Tier" },
  { key: "last_seen", label: "Laatst actief" },
];

const fmt = (v) => (v === null || v === undefined ? "—" : typeof v === "number" ? v.toLocaleString("nl-NL") : v);
const dateOnly = (iso) => (iso ? iso.slice(0, 10) : "—");
const dateTime = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

const Kpi = ({ label, value, hint, testid, Icon = Users, tint = "bg-brandblue-light text-brandblue" }) => (
  <div className="rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid={testid} title={hint}>
    <div className="flex items-center justify-between">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}><Icon size={16} /></div>
      {hint && <Info size={12} className="text-slate-300" />}
    </div>
    <div className="mt-2 font-display text-2xl font-extrabold text-navy leading-none">{fmt(value)}</div>
    <div className="mt-1 text-sm font-semibold text-navy">{label}</div>
    {hint && <div className="mt-0.5 text-[11px] leading-tight text-slate-500">{hint}</div>}
  </div>
);

const SectionHeader = ({ title, subtitle }) => (
  <div className="mt-6">
    <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500">{title}</h2>
    {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
  </div>
);

const AdminUsers = () => {
  useSeo({ title: "Gebruikers · Admin" });
  const nav = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null); setErr("");
    const params = new URLSearchParams({ period, tier, status, sort, page, page_size: pageSize });
    if (q.trim()) params.set("q", q.trim());
    api(`/admin/users/list?${params}`).then(setData).catch((e) => setErr(e.message));
  }, [period, tier, status, sort, page, pageSize, q, nonce]);

  const pages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  const exportCsv = () => {
    const params = new URLSearchParams({ period, tier, status });
    if (q.trim()) params.set("q", q.trim());
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("wa_token") || "";
    // Fetch with auth header, then trigger download
    fetch(`${base}/api/admin/users/export.csv?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url;
        a.download = `woningalert-users-${period}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const k = data?.kpis || {};

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/admin" data-testid="back-admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={14} /> Admin</Link>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-navy" data-testid="admin-users-title">Gebruikers</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv"><Download size={14} className="mr-1" /> CSV</Button>
          <Button variant="ghost" size="sm" onClick={() => setNonce((n) => n + 1)} data-testid="refresh-btn"><RefreshCw size={14} className="mr-1" /> Ververs</Button>
        </div>
      </div>

      {/* KPI: Accounts overzicht */}
      <SectionHeader title="Accounts overzicht" subtitle="Totaalstand van je gebruikersbestand op dit moment." />
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        <Kpi label="Totaal accounts" hint="Alle geregistreerde accounts (excl. verwijderd)." value={k.total_accounts} testid="kpi-total" Icon={Users} />
        <Kpi label="Premium abonnees" hint="Actieve betalende Premium-users (admins niet meegeteld)." value={k.total_premium} testid="kpi-premium" Icon={Crown} tint="bg-amber-100 text-amber-600" />
        <Kpi label="Free gebruikers" hint="Accounts zonder actief Premium-abonnement." value={k.total_free} testid="kpi-free" Icon={UserCircle2} tint="bg-slate-100 text-slate-600" />
      </div>

      {/* KPI: Nieuwe inschrijvingen */}
      <SectionHeader title="Nieuwe inschrijvingen" subtitle="Aantal nieuwe accounts aangemaakt in de gekozen periode." />
      <div className="mt-2 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Laatste 24 uur" hint="Signups in de afgelopen 24 uur." value={k.new_24h} testid="kpi-24h" Icon={Clock} tint="bg-emerald-50 text-emerald-600" />
        <Kpi label="Laatste 7 dagen" hint="Signups in de afgelopen 7 dagen." value={k.new_7d} testid="kpi-7d" Icon={CalendarDays} tint="bg-emerald-50 text-emerald-600" />
        <Kpi label="Laatste 14 dagen" hint="Signups in de afgelopen 14 dagen." value={k.new_14d} testid="kpi-14d" Icon={CalendarRange} tint="bg-emerald-50 text-emerald-600" />
        <Kpi label="Laatste 30 dagen" hint="Signups in de afgelopen 30 dagen." value={k.new_30d} testid="kpi-30d" Icon={CalendarCheck2} tint="bg-brandblue-light text-brandblue" />
        <Kpi label="Laatste 60 dagen" hint="Signups in de afgelopen 60 dagen." value={k.new_60d} testid="kpi-60d" Icon={CalendarClock} tint="bg-brandblue-light text-brandblue" />
        <Kpi label="Laatste 90 dagen" hint="Signups in de afgelopen 90 dagen." value={k.new_90d} testid="kpi-90d" Icon={CalendarClock} tint="bg-brandblue-light text-brandblue" />
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="filter-bar">
        {PERIODS.map((p) => (
          <button key={p.key} type="button" data-testid={`period-${p.key}`}
            onClick={() => { setPeriod(p.key); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm font-semibold ${period === p.key ? "bg-navy text-amber-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {p.label}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-border" />
        {TIERS.map((tt) => (
          <button key={tt.key} type="button" data-testid={`tier-${tt.key}`}
            onClick={() => { setTier(tt.key); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm font-semibold ${tier === tt.key ? "bg-brandblue text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {tt.label}
          </button>
        ))}
        <select data-testid="status-select" className="rounded-full border border-border bg-white px-3 py-1.5 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select data-testid="sort-select" className="rounded-full border border-border bg-white px-3 py-1.5 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map((s) => <option key={s.key} value={s.key}>Sorteer: {s.label}</option>)}
        </select>
      </div>

      {/* Search */}
      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="user-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Zoek op naam, e-mail of user ID…" className="pl-9 rounded-full" />
        </div>
      </div>

      {err && <div className="mt-4 rounded-xl bg-danger/10 p-3 text-sm text-danger">{err}</div>}

      {/* Table */}
      <div className="mt-6 overflow-x-auto rounded-3xl border border-border bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-4 py-3">Naam</th>
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Aangemaakt</th>
              <th className="px-4 py-3">Laatst actief</th>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Consent</th>
              <th className="px-4 py-3">Profiel</th>
              <th className="px-4 py-3">Favs</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={11} className="p-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></td></tr>}
            {data?.users?.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-400">Geen gebruikers voor deze filter.</td></tr>}
            {data?.users?.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{u.name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{dateTime(u.created_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{dateTime(u.last_active_at || u.last_login_at)}</td>
                <td className="px-4 py-3">
                  {u.is_premium ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                      <Sparkles size={10} /> Premium
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Free</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{u.subscription_status || "—"}</td>
                <td className="px-4 py-3 text-xs">{u.marketing_consent ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-xs">{u.has_profile ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-xs">{u.favorites_count}</td>
                <td className="px-4 py-3 text-xs">{u.acquisition_source}</td>
                <td className="px-4 py-3">
                  <Link to={`/admin/users/${u.id}`} data-testid={`user-view-${u.id}`} className="inline-flex items-center gap-1 text-brandblue hover:underline">
                    <Eye size={13} /> Bekijk
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > pageSize && (
        <div className="mt-4 flex items-center justify-between gap-2" data-testid="pagination">
          <div className="text-xs text-slate-500">Totaal {fmt(data.total)} · pagina {page} / {pages}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev"><ChevronLeft size={14} /></Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="page-next"><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
    <h3 className="font-display text-lg font-extrabold text-navy">{title}</h3>
    <div className="mt-3 space-y-2 text-sm">{children}</div>
  </div>
);
const DetailRow = ({ k, v }) => (
  <div className="grid grid-cols-[140px,1fr] gap-3">
    <span className="text-slate-500">{k}</span>
    <span className="font-medium text-navy">{v == null || v === "" ? "—" : typeof v === "string" || typeof v === "number" ? v : v}</span>
  </div>
);

export const AdminUserDetail = () => {
  const { uid } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useSeo({ title: "Gebruiker · Admin" });
  useEffect(() => {
    api(`/admin/users/${uid}/detail`).then(setD).catch((e) => setErr(e.message));
  }, [uid]);
  if (err) return <div className="mx-auto max-w-3xl p-8"><div className="rounded-xl bg-danger/10 p-4 text-danger">{err}</div></div>;
  if (!d) return <div className="mx-auto max-w-3xl p-8 text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="admin-user-detail">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={14} /> Gebruikers</Link>
      <div className="mt-2 flex items-center gap-3">
        <h1 className="font-display text-3xl font-extrabold text-navy">{d.user.name || d.user.email}</h1>
        {d.subscription.is_premium && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-navy px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-amber-300">
            <Sparkles size={12} /> Premium
          </span>
        )}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <DetailSection title="Account">
          <DetailRow k="Naam" v={d.user.name} />
          <DetailRow k="E-mail" v={d.user.email} />
          <DetailRow k="User ID" v={<code className="font-mono text-xs">{d.user.id}</code>} />
          <DetailRow k="Aangemaakt" v={dateTime(d.user.created_at)} />
          <DetailRow k="Laatst ingelogd" v={dateTime(d.user.last_login_at)} />
          <DetailRow k="Laatst actief" v={dateTime(d.user.last_active_at)} />
          <DetailRow k="Signup platform" v={d.user.signup_platform} />
          <DetailRow k="Role" v={d.user.role} />
          <DetailRow k="Marketing consent" v={d.user.marketing_consent ? `Ja (${dateTime(d.user.marketing_consent_at)})` : "Nee"} />
          <DetailRow k="Unsubscribed" v={d.user.unsubscribed_at ? `Ja (${dateTime(d.user.unsubscribed_at)})` : "Nee"} />
          <DetailRow k="Acquisition" v={d.acquisition_source} />
        </DetailSection>

        <DetailSection title="Abonnement">
          <DetailRow k="Tier" v={d.subscription.tier} />
          <DetailRow k="Status" v={d.subscription.status} />
          <DetailRow k="Premium?" v={d.subscription.is_premium ? "Ja" : "Nee"} />
          <DetailRow k="Provider" v={d.subscription.provider} />
          <DetailRow k="Stripe sub id" v={d.subscription.stripe_subscription_id ? <code className="font-mono text-xs">{d.subscription.stripe_subscription_id}</code> : "—"} />
          <DetailRow k="Lifecycle stages" v={(d.subscription.lifecycle_stages_sent || []).join(", ") || "—"} />
        </DetailSection>

        <DetailSection title="Zoekprofiel">
          {d.profile ? (
            <>
              <DetailRow k="Steden" v={(d.profile.cities || []).join(", ")} />
              <DetailRow k="Max prijs" v={d.profile.max_price ? `€ ${d.profile.max_price}` : null} />
              <DetailRow k="Min m²" v={d.profile.min_m2} />
              <DetailRow k="Kamers" v={d.profile.bedrooms} />
              <DetailRow k="Type" v={d.profile.property_type} />
              <DetailRow k="Gemeubileerd" v={d.profile.furnished} />
              <DetailRow k="Huisdieren" v={d.profile.pets} />
              <DetailRow k="Huishouden" v={d.profile.household} />
              <DetailRow k="Verhuisdatum" v={d.profile.move_date} />
            </>
          ) : (
            <p className="text-slate-500">Nog geen zoekprofiel aangemaakt.</p>
          )}
        </DetailSection>

        <DetailSection title="Activiteit">
          <div className="grid grid-cols-3 gap-3">
            <div><div className="text-xs text-slate-400">Favorieten</div><div className="font-bold text-navy">{d.counters.favorites}</div></div>
            <div><div className="text-xs text-slate-400">Alerts</div><div className="font-bold text-navy">{d.counters.alerts}</div></div>
            <div><div className="text-xs text-slate-400">Scam Checks</div><div className="font-bold text-navy">{d.counters.scam_checks}</div></div>
          </div>
          <div className="mt-4 max-h-[280px] overflow-y-auto rounded-2xl border border-border bg-slate-50/60 p-3">
            {d.timeline.length === 0 && <p className="text-xs text-slate-400">Geen events.</p>}
            {d.timeline.slice(0, 100).map((t, i) => (
              <div key={i} className="flex gap-2 border-b border-border/60 py-1.5 last:border-0 text-xs">
                <span className="w-32 shrink-0 font-mono text-slate-500">{dateTime(t.ts)}</span>
                <span className="w-40 shrink-0 font-semibold text-navy">{t.kind}</span>
                <span className="text-slate-500">{t.screen || ""}</span>
              </div>
            ))}
          </div>
        </DetailSection>
      </div>
    </div>
  );
};

export default AdminUsers;
